export type FieldType =
  // Tipos genéricos
  | "string"
  | "number"
  | "boolean"
  | "file"
  | "file_list"
  | "date"
  | "select"
  | "multi_select"
  // Dados pessoais do candidato (Gupy)
  | "cpf"
  | "full_name"
  | "email"
  | "phone"
  | "birth_date"
  // Endereço (Gupy)
  | "cep"
  | "address_street"
  | "address_number"
  | "address_city"
  | "address_state"
  // Vaga (Gupy)
  | "job_title"
  | "job_department"
  // Outros (Gupy)
  | "pcd";

export interface FieldOption {
  value: string;
  label: string;
}

export type RelativeUnit = "days" | "months" | "years";

export interface MaxFromToday {
  amount: number;
  unit: RelativeUnit;
}

export interface DateValidationRules {
  min_date?: string;
  max_date?: string;
  min_age?: number;
  max_age?: number;
  allowed_weekdays?: string[];
  max_from_today?: MaxFromToday;
}

export interface NumberValidationRules {
  min?: number;
  max?: number;
  integer_only?: boolean;
}

export type ValidationRules = DateValidationRules | NumberValidationRules;

export const isDateRules = (r: ValidationRules): r is DateValidationRules =>
  "min_date" in r || "max_date" in r || "min_age" in r || "max_age" in r
  || "allowed_weekdays" in r || "max_from_today" in r;

export const isNumberRules = (r: ValidationRules): r is NumberValidationRules =>
  "min" in r || "max" in r || "integer_only" in r;

export interface VisibilityCondition {
  field_key: string;
  operator: "equals" | "not_equals" | "contains" | "in" | "not_in" | "is_empty" | "is_not_empty";
  value: string | string[];
}

export interface VisibilityRules {
  conditions: VisibilityCondition[];
  logic: "AND" | "OR";
}

export interface ReferenceSource {
  field_key: string;  // key do campo em qualquer missão — resolvido globalmente pelo backend
}

export interface MissionField {
  id: number;
  key: string;
  label: string;
  details: string | null;
  field_type: FieldType;
  is_required: boolean;
  is_pj: boolean;
  is_clt: boolean;
  order: number;
  options: FieldOption[] | null;
  validation_rules: ValidationRules | null;
  visibility_rules: VisibilityRules | null;
  reference_source: ReferenceSource | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MissionDefinition {
  id: number;
  slug: string;
  title: string;
  description: string;
  order: number;
  is_required: boolean;
  tech_only: boolean;
  is_pj: boolean;
  is_clt: boolean;
  is_active: boolean;
  requires_internal_wait: boolean;
  alert_on_completion: boolean;
  created_at: string;
  updated_at: string;
  fields: MissionField[];
}

export interface CreateMissionPayload {
  slug: string;
  title: string;
  description: string;
  order: number;
  is_required: boolean;
  tech_only: boolean;
  is_pj: boolean;
  is_clt: boolean;
  requires_internal_wait: boolean;
  alert_on_completion: boolean;
  fields?: never[];
}

export interface UpdateMissionPayload {
  title?: string;
  description?: string;
  order?: number;
  is_required?: boolean;
  tech_only?: boolean;
  is_pj?: boolean;
  is_clt?: boolean;
  is_active?: boolean;
  requires_internal_wait?: boolean;
  alert_on_completion?: boolean;
}

export interface CreateFieldPayload {
  key: string;
  label: string;
  details?: string | null;
  field_type: FieldType;
  is_required: boolean;
  is_pj: boolean;
  is_clt: boolean;
  order: number;
  options: FieldOption[] | null;
  validation_rules: ValidationRules | null;
  visibility_rules: VisibilityRules | null;
  reference_source: ReferenceSource | null;
}

export interface UpdateFieldPayload {
  label?: string;
  details?: string | null;
  field_type?: FieldType;
  is_required?: boolean;
  is_pj?: boolean;
  is_clt?: boolean;
  order?: number;
  options?: FieldOption[] | null;
  validation_rules?: ValidationRules | null;
  visibility_rules?: VisibilityRules | null;
  reference_source?: ReferenceSource | null;
  is_active?: boolean;
}

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  // Genéricos
  string:       "Texto livre",
  number:       "Número",
  boolean:      "Sim / Não",
  file:         "Arquivo",
  file_list:    "Lista de Arquivos",
  date:         "Data",
  select:       "Seleção única",
  multi_select: "Multi-seleção",
  // Gupy — dados pessoais
  full_name:    "Nome completo",
  email:        "E-mail",
  cpf:          "CPF",
  phone:        "Telefone",
  birth_date:   "Data de nascimento",
  // Gupy — endereço
  cep:           "CEP",
  address_street: "Logradouro",
  address_number: "Número (endereço)",
  address_city:   "Cidade",
  address_state:  "Estado (UF)",
  // Gupy — vaga
  job_title:      "Cargo da vaga",
  job_department: "Departamento",
  // Gupy — outros
  pcd: "PCD (Pessoa com Deficiência)",
};

/** Tipos genéricos — preenchidos livremente pelo candidato. */
export const STANDARD_FIELD_TYPES: FieldType[] = [
  "string", "number", "boolean", "file", "file_list", "date", "select", "multi_select",
];

/**
 * Tipos especiais vinculados ao perfil do candidato na plataforma Gupy.
 * Serão pré-preenchidos automaticamente com os dados da whitelist.
 */
export const GUPY_FIELD_TYPES: FieldType[] = [
  // Dados pessoais
  "full_name", "email", "cpf", "phone", "birth_date",
  // Endereço
  "cep", "address_street", "address_number", "address_city", "address_state",
  // Vaga
  "job_title", "job_department",
  // Outros
  "pcd",
];

/** @deprecated use GUPY_FIELD_TYPES */
export const PREFILLED_FIELD_TYPES: FieldType[] = GUPY_FIELD_TYPES;
